import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import type { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import {
  getCabinCategories,
  getCruiseCountriesByItinerary,
  getCruiseOfferMemberPriceVariation,
  getCruiseOfferPriceVariation,
  getCruisePortsByItinerary,
} from 'lib/cruises/cruiseUtils'
import { checkHasOnlySuitesAvailable, getHighlightedDepartureDate } from 'lib/cruises/cruiseOfferUtils'
import { isVelocityEnabled as getVelocityEnabled } from 'selectors/featuresSelectors'
import { useCruiseOfferInclusions } from 'hooks/Cruise/useCruiseOfferInclusions'
import { useCruiseMemberPricing } from 'hooks/Cruise/useCruiseMemberPricing'
import moment from 'moment'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import { isCruiseOffer, isCruiseV1Offer } from 'lib/offer/offerTypes'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { useQsQueryParams } from 'hooks/useQueryParams'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'

interface CommonCruiseOfferView<Offer extends App.CruiseOffer | App.TourOffer | App.TourOfferSummary> {
  offer: Offer
  offerTitle: string
  offerDescription?: string
  departureCity: string
  destinationCity: string
  cities: Array<string>
  countries: Array<string>
  duration: number
  offerImages: Array<App.Image>
  lowestPrice: CruiseOfferPriceVariation
  memberLowestPrice?: CruiseOfferPriceVariation
  canSeeLuxPlusLabel: boolean
  canSeeLuxPlusPrice: boolean
  hasOnlySuitesAvailable: boolean
  isVelocityEnabled: boolean
  highlightedDepartureId?: string
  earliestDepartureDate?: string
  laterDates: Array<string>
  departureDates: Array<string>
  shipName: string
  cruiseLineName: string
  cruiseLineImage: string | undefined
  isSpecialOffer: boolean
  shipDetailsLabel: string
  itineraryDetailsLabel: string
  productType: App.ProductType
  evergreenInclusions: Array<App.EvergreenInclusion>
  disableDeposits: boolean
  isFlashOffer: boolean
  cabinCategory: string
  discountPills?: App.CruiseDiscountPillDetails
  itinerary?: Array<App.CruiseItineraryItem>
  itineraryFlash?: string
  tileInclusions?: Array<App.OfferInclusion>
  inclusions: Array<App.OfferInclusion>
  inclusionsTitle?: string
  urgencyLabels: Array<App.OfferUrgencyLabel>
  isNew?: boolean
  isEndingIn?: number
  inclusionEndDate?: string
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  /** This needs to be optional as we use the same model for tour offers */
  cruiseType?: App.CruiseType
}

export interface CruiseOnlyOfferView extends CommonCruiseOfferView<App.CruiseOffer> {
  availableCabinCategoriesPriceVariations: Map<string, CruiseOfferPriceVariation>
}

interface LegacyCruiseOfferView extends CommonCruiseOfferView<App.TourOffer | App.TourOfferSummary> {
  availableCabinCategoriesPriceVariations?: undefined
}

export type CruiseOfferView = CruiseOnlyOfferView | LegacyCruiseOfferView

export function isCruiseOnlyOfferView(view: CruiseOfferView): view is CruiseOnlyOfferView {
  return isCruiseOffer(view.offer)
}

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  cabinTypes?: string
  filters?: App.OfferListFilters
}

interface CruiseLowestPriceSelection {
  departureId: string
  price?: App.CruisePriceDetails | App.LowestPriceWithCategory
}

function getCruiseMemberPriceValueByCategory(
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory> | undefined,
  cabinCategory: string | undefined,
): number | undefined {
  if (!departureMemberPricingByCategory || !cabinCategory) {
    return undefined
  }
  return departureMemberPricingByCategory[cabinCategory.toLowerCase()]?.memberPrice
}

function useCruiseOfferView({ offer, cabinTypes, filters }: Props): CruiseOfferView {
  const intl = useIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const globalFilters = useContext(GlobalSearchStateContext)
  const { SORT_DEPARTURE_SOON } = useOfferListSortOptions()
  const cruisePriceByNight = useCruisePriceByNight()
  const { sortBy } = useQsQueryParams<{ sortBy?: string }>()
  const isVelocityEnabled = useAppSelector(getVelocityEnabled)
  const isCurrentCruiseOffer = isCruiseOffer(offer)
  const mainDepartureDetails = 'mainDepartureDetails' in offer ? offer.mainDepartureDetails : undefined
  const { departureMemberPricing, canSeeLuxPlusLabel, canSeeLuxPlusPrice } = useCruiseMemberPricing(
    isCurrentCruiseOffer ? offer : undefined,
    mainDepartureDetails,
  )
  const { standardInclusions, luxPlusInclusions, inclusionDetails } = useCruiseOfferInclusions({
    departure: mainDepartureDetails!,
  })

  const lowestPriceSelection = useMemo<CruiseLowestPriceSelection | undefined>(() => {
    if (!isCurrentCruiseOffer) {
      return undefined
    }

    let departureId = offer.mainDepartureDetails.id
    let lowestOffer: App.CruisePriceDetails | App.LowestPriceWithCategory | undefined =
      offer.mainDepartureDetails.lowestOverallPriceDetails

    if (cabinTypes) {
      lowestOffer = offer.mainDepartureDetails.lowestPriceDetailsByCategory[cabinTypes]
    }

    /* The returned price must match the active MIN/MAX price filter to ensure
     * no out-of-range price is displayed to the user. */
    const minPrice = filters?.minPrice
    const maxPrice = filters?.maxPrice
    const isSortDesc = sortBy === 'price.desc'

    if (minPrice || maxPrice || isSortDesc) {
      const allDeparturePrices: Array<{
        departureId: string
        price: App.LowestPriceWithCategory
      }> = Object.entries(offer.lowestDeparturePricesByCategory)
        .flatMap(([candidateDepartureId, categoryPrices]) =>
          Object.values(categoryPrices).map((price) => ({
            departureId: candidateDepartureId,
            price,
          })),
        )

      const priceForComparison = cruisePriceByNight
        ? ({ price }: { price: App.LowestPriceWithCategory }) => Math.round(price.total / offer.duration)
        : ({ price }: { price: App.LowestPriceWithCategory }) => price.total

      const qualifying = allDeparturePrices
        .filter(({ price }) => {
          if (cabinTypes && price.cabinCategory !== cabinTypes) return false
          const priceForFilter = priceForComparison({ price })
          if (minPrice && priceForFilter < minPrice) return false
          if (maxPrice && priceForFilter > maxPrice) return false
          return true
        })
        .sort((left, right) => {
          const sortOrder = isSortDesc ? -1 : 1
          return (priceForComparison(left) - priceForComparison(right)) * sortOrder
        })

      if (qualifying.length > 0) {
        lowestOffer = qualifying[0].price
        departureId = qualifying[0].departureId
      }
    }

    return {
      departureId,
      price: lowestOffer,
    }
  }, [cruisePriceByNight, isCurrentCruiseOffer, offer, cabinTypes, filters?.minPrice, filters?.maxPrice, sortBy])

  const lowestPrice = useMemo((): CruiseOfferPriceVariation => {
    if (isCurrentCruiseOffer) {
      return getCruiseOfferPriceVariation(offer, lowestPriceSelection?.price, cruisePriceByNight)
    }

    const saleUnit = cruisePriceByNight ? 'night' : offer.saleUnit.toLowerCase() === 'cruise' ? 'person' : offer.saleUnit
    const lowestPrice = offer.lowestPricePackage?.price || 0
    const total = cruisePriceByNight ? Math.round(lowestPrice / offer.minDuration! || 1) : lowestPrice
    return {
      primaryPrice: {
        total,
        type: saleUnit,
      },
    }
  }, [cruisePriceByNight, isCurrentCruiseOffer, lowestPriceSelection?.price, offer])

  const memberLowestPrice = useMemo(() => {
    if (!isCurrentCruiseOffer) {
      return undefined
    }

    const departureMemberPricingByCategory = lowestPriceSelection?.departureId
      ? departureMemberPricing
        ?.find((d) => d.id === lowestPriceSelection.departureId)
        ?.lowestPricesByCategory
      : undefined

    const memberPrice = getCruiseMemberPriceValueByCategory(
      departureMemberPricingByCategory,
      lowestPriceSelection?.price?.cabinCategory,
    )

    return getCruiseOfferMemberPriceVariation(offer, memberPrice, cruisePriceByNight)
  }, [
    departureMemberPricing,
    offer,
    cruisePriceByNight,
    lowestPriceSelection?.departureId,
    lowestPriceSelection?.price?.cabinCategory,
    isCurrentCruiseOffer,
  ])

  const departureDates = useMemo(() => {
    if (isCruiseOffer(offer)) {
      const { mainDepartureDetails } = offer
      const allDepartureDates = (offer.departureDates ?? [])
        .map((date) => moment(date))
        .sort((a, b) => a.unix() - b.unix())
        .map((date) => date.format(DMY_CASUAL_FORMAT))

      if (mainDepartureDetails.departureDate) {
        const mainDepartureDate = moment(mainDepartureDetails.departureDate).format(DMY_CASUAL_FORMAT)
        return [
          ...new Set([mainDepartureDate, ...allDepartureDates]),
        ]
      }

      return allDepartureDates
    }
    const departureDates: Array<string> = []

    if (offer.travelFromDate) {
      departureDates.push(offer.travelFromDate)
    }

    if (offer.travelFromDate && offer.travelToDate && (offer.travelToDate !== offer.travelFromDate)) {
      departureDates.push(offer.travelToDate)
    }

    return departureDates
      .filter(Boolean)
      .map((date) => moment(date).format(DMY_CASUAL_FORMAT))
  }, [offer])

  const [earliestDepartureDate, ...laterDates] = departureDates

  const offerDescription = useMemo(() => {
    if (isCruiseOffer(offer)) {
      const shipInfoDescription = offer.ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
      const cruiseDescription = offer.description || shipInfoDescription || offer.destinationDescription
      const parsedDescription = cruiseDescription
        ?.replace(/(<([^>]+)>)/gi, '')
        ?.split(' ')
        ?.slice(0, 35)
        ?.join(' ') + '...'

      return parsedDescription
    }
  }, [offer])

  const isSpecialOffer = isCruiseOffer(offer) ? !!(offer.evergreenInclusions.length || standardInclusions.length) : false

  const urgencyLabels = useMemo<Array<App.OfferUrgencyLabel>>(() => {
    const tags: Array<App.OfferUrgencyLabel> = []

    if (isCruiseV1Offer(offer)) {
      if (offer.lowestPricePackage?.memberPrice) {
        tags.push({
          type: 'lux_plus_member_price',
          saveAmount: offer.lowestPricePackage.price - offer.lowestPricePackage.memberPrice,
        })
      }
      if (offer.isAgentHubExclusive) {
        tags.push({ type: 'agenthub_exclusive' })
      }
    }

    if (isSpecialOffer) {
      tags.push({
        type: 'sale',
        message: intl.formatMessage({
          id: 'FwPOMzUGIhSV8PQk7AwD',
          defaultMessage: 'Special offer',
          description: 'Urgency label tag message for cruise offers with special promotions or inclusions',
        }),
      })
    }

    return tags
  }, [intl, offer, isSpecialOffer])

  const inclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (isCruiseOffer(offer)) {
      const evergreenInclusions = offer.evergreenInclusions.map<App.OfferInclusion>(incl => ({
        id: incl.id,
        description: incl.description,
        isHighlighted: true,
        symbol: 'line_key_skeleton',
      }))

      return evergreenInclusions
    } else {
      return offer.tileInclusions ?? []
    }
  }, [offer])

  const inclusionsTitle = useMemo<string | undefined>(() => {
    if (isCruiseOffer(offer) && offer.evergreenInclusions.length) {
      return intl.formatMessage({
        id: 'rwkcKHyT1_UOYotRXMQ7',
        defaultMessage: 'Inclusions (select packages only)',
        description: 'Inclusions section title for cruise offers with package-specific inclusions',
      })
    }
  }, [offer, intl])

  const view = useMemo<CruiseOfferView>(() => {
    if (isCruiseOffer(offer)) {
      const departureCity = offer.departurePort.split(',')[0]
      const destinationCity = offer.returnPort.split(',')[0]
      const cities = getCruisePortsByItinerary(offer.itinerary)
      const countries = getCruiseCountriesByItinerary(offer.itinerary)
      const detailItems = [offer.cruiseLine.name, offer.ship.name].filter(Boolean)
      const itineraryDetailItems: Array<string> = []
      const duration = offer.duration

      if (duration) {
        itineraryDetailItems.push(intl.formatMessage({
          id: 'p_AmFxX6kDvJXY5bArwK',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Cruise offer itinerary duration in nights in the cruise section',
        }, { count: duration }))
      }
      if (cities.length) {
        itineraryDetailItems.push(intl.formatMessage({
          id: 'MEOykOkHkOHQjgE4Kq-_',
          defaultMessage: '{count, plural, one {# port} other {# ports}}',
          description: 'Cruise offer itinerary port count in the cruise offer or booking section',
        }, { count: cities.length }))
      }
      if (countries.length) {
        itineraryDetailItems.push(intl.formatMessage({
          id: '0W48OTMrKFzvri0LkkQt',
          defaultMessage: '{count, plural, one {# country} other {# countries}}',
          description: 'Cruise offer itinerary country count in the cruise offer or booking section',
        }, { count: countries.length }))
      }

      const isSortingByDepartureSoon = sortBy === SORT_DEPARTURE_SOON.value
      const { highlightedDepartureId, highlightedDepartureDate, otherDepartureDates } = getHighlightedDepartureDate(
        offer.mainDepartureDetails.departureDate ?? undefined,
        offer.departuresSummary,
        globalFilters.checkinDate,
        globalFilters.checkoutDate,
        globalFilters.flexibleMonths,
        isSortingByDepartureSoon,
      )
      const hasOnlySuitesAvailable = checkHasOnlySuitesAvailable(highlightedDepartureId, offer)

      const { lowestDeparturePricesByCategory, mainDepartureDetails } = offer
      const { lowestPriceDetailsByCategory } = mainDepartureDetails
      const lowestDeparturePriceByCategory = highlightedDepartureId ? lowestDeparturePricesByCategory[highlightedDepartureId] : undefined
      const cabinCategoriesBasedOnCruiseType = getCabinCategories(offer.cruiseType)

      const availableCabinCategoriesPriceVariations = new Map<string, CruiseOfferPriceVariation>(
        cabinCategoriesBasedOnCruiseType
          .map(cabinCategory => {
            const prices = lowestDeparturePriceByCategory ?? lowestPriceDetailsByCategory
            const price = prices[cabinCategory.category]

            return [
              cabinCategory.category,
              getCruiseOfferPriceVariation(offer, price, cruisePriceByNight),
            ] as const
          })
          .filter(([, price]) => !!price.primaryPrice.total),
      )

      const view: CruiseOfferView = {
        offerTitle: offer.name,
        offerDescription,
        departureCity,
        destinationCity,
        cities,
        countries,
        duration,
        offerImages: offer.images,
        lowestPrice,
        memberLowestPrice,
        canSeeLuxPlusLabel,
        canSeeLuxPlusPrice,
        hasOnlySuitesAvailable,
        isVelocityEnabled,
        highlightedDepartureId,
        earliestDepartureDate: highlightedDepartureDate,
        laterDates: otherDepartureDates,
        departureDates,
        shipName: offer.ship.name,
        cruiseLineName: offer.cruiseLine.name,
        cruiseLineImage: offer.cruiseLine.imageId,
        isSpecialOffer,
        shipDetailsLabel: detailItems.join(` ${sep} `),
        itineraryDetailsLabel: itineraryDetailItems.join(` ${sep} `),
        productType: offer.productType,
        evergreenInclusions: offer.evergreenInclusions,
        disableDeposits: offer.disableDeposits,
        isFlashOffer: false,
        cabinCategory: cabinTypes || offer.mainDepartureDetails.lowestOverallPriceDetails.cabinCategory,
        discountPills: offer.mainDepartureDetails.lowestOverallPriceDetails.discountPills ?? undefined,
        itinerary: offer.itinerary,
        offer,
        urgencyLabels,
        inclusions,
        inclusionsTitle,
        availableCabinCategoriesPriceVariations,
        isNew: inclusionDetails?.hasNewInclusion || false,
        inclusionEndDate: inclusionDetails?.inclusionEndDate,
        standardInclusions,
        luxPlusInclusions,
        cruiseType: offer.cruiseType,
      }
      return view
    }

    const shipName = offer.vendorVehicle ?? ''
    const cruiseLineName = offer.vendorName ?? ''
    const cruiseLineImage = offer.lowestPricePackage?.tour?.logoImageId || ''
    const duration = offer.minDuration ?? 0
    const departureCity = offer.startLocation?.split(',')?.[0] ?? ''
    const destinationCity = offer.endLocation?.split(',')?.[0] ?? ''
    const locations = offer.locationsVisited ?? []
    const countries = unique(nonNullable(locations.map(({ country }) => country)))
    const cities = unique(nonNullable(locations.map(({ cityOrPlaceName }) => cityOrPlaceName)))

    const detailItems: Array<string> = []

    if (duration) {
      // FOR FLASH CRUISES WE NEED TO
      // SUBTRACT 1 NIGHT FROM THE DURATION.
      // BECAUSE DURATION COMES IN DAYS RATHER THATN NIGHTS.
      // E.G. 7 DAYS CRUISE IS 6 NIGHTS
      const nights = duration - 1
      detailItems.push(intl.formatMessage({
        id: 'S5MCd2a2ldG8Aff_-iKg',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Flash cruise offer duration in nights in the cruise offer or booking section',
      }, { count: nights }))
    }

    if (cities.length) {
      detailItems.push(intl.formatMessage({
        id: 'MC9KrrygKsvF8OG9-JtM',
        defaultMessage: '{count, plural, one {# port} other {# ports}}',
        description: 'Pluralised count of ports included in the flash cruise offer itinerary listing',
      }, { count: cities.length }))
    }

    if (countries.length) {
      detailItems.push(intl.formatMessage({
        id: 'Ob8ARewHoDB5az960ohJ',
        defaultMessage: '{count, plural, one {# country} other {# countries}}',
        description: 'Flash cruise offer country count in the cruise offer or booking section',
      }, { count: countries.length }))
    }

    const itineraryDetailsLabel = detailItems.join(` ${sep} `)
    const shipDetailsItems = [cruiseLineName, shipName].filter(Boolean)
    const shipDetailsLabel = shipDetailsItems.join(` ${sep} `)

    const itineraryFlash = offer.lowestPricePackage?.tour?.itinerary || ''

    const view: CruiseOfferView = {
      offerTitle: offer.name,
      shipName,
      cruiseLineName,
      cruiseLineImage,
      departureCity,
      destinationCity,
      duration,
      offerImages: offer.images,
      lowestPrice,
      memberLowestPrice,
      canSeeLuxPlusLabel,
      canSeeLuxPlusPrice,
      hasOnlySuitesAvailable: false,
      shipDetailsLabel,
      itineraryDetailsLabel,
      isVelocityEnabled,
      earliestDepartureDate,
      laterDates,
      cities,
      countries,
      itineraryFlash,
      departureDates,
      isSpecialOffer,
      productType: 'cruise-flash',
      offerDescription: offer.preHeader,
      disableDeposits: true,
      isFlashOffer: true,
      tileInclusions: offer.tileInclusions,
      inclusions,
      inclusionsTitle,
      cabinCategory: '',
      offer,
      urgencyLabels,
      evergreenInclusions: [],
      isNew: false,
      luxPlusInclusions,
      standardInclusions,
    }
    return view
  }, [
    cabinTypes,
    cruisePriceByNight,
    sep,
    departureDates,
    earliestDepartureDate,
    globalFilters.checkinDate,
    globalFilters.checkoutDate,
    globalFilters.flexibleMonths,
    inclusionDetails?.inclusionEndDate,
    inclusionDetails?.hasNewInclusion,
    inclusions,
    inclusionsTitle,
    isSpecialOffer,
    isVelocityEnabled,
    laterDates,
    lowestPrice,
    memberLowestPrice,
    canSeeLuxPlusLabel,
    canSeeLuxPlusPrice,
    luxPlusInclusions,
    offer,
    offerDescription,
    sortBy,
    SORT_DEPARTURE_SOON,
    standardInclusions,
    urgencyLabels,
    intl,
  ])
  return view
}

export default useCruiseOfferView
